import axios from 'axios';
import { safeLocalStorage } from '../utils/safeStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api/marketing`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = safeLocalStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Contacts
export const getContacts = (params = {}) => api.get('/contacts', { params });
export const getContact = (id) => api.get(`/contacts/${id}`);
export const createContact = (data) => api.post('/contacts', data);
export const updateContact = (id, data) => api.put(`/contacts/${id}`, data);
export const deleteContact = (id) => api.delete(`/contacts/${id}`);
export const bulkCreateContacts = (contacts) => api.post('/contacts/bulk', contacts);

// Groups
export const getGroups = (params = {}) => api.get('/groups', { params });
export const getGroup = (id) => api.get(`/groups/${id}`);
export const createGroup = (data) => api.post('/groups', data);
export const updateGroup = (id, data) => api.put(`/groups/${id}`, data);
export const deleteGroup = (id) => api.delete(`/groups/${id}`);

// Campaigns
export const getCampaigns = (params = {}) => api.get('/campaigns', { params });
export const getCampaign = (id) => api.get(`/campaigns/${id}`);
export const createCampaign = (data) => api.post('/campaigns', data);
export const updateCampaign = (id, data) => api.put(`/campaigns/${id}`, data);
export const deleteCampaign = (id) => api.delete(`/campaigns/${id}`);
export const startCampaign = (id) => api.post(`/campaigns/${id}/start`);
export const pauseCampaign = (id) => api.post(`/campaigns/${id}/pause`);
export const scheduleCampaign = (id, data) => api.post(`/campaigns/${id}/schedule`, data);

// Polls
export const getPolls = (params = {}) => api.get('/polls', { params });
export const getPoll = (id) => api.get(`/polls/${id}`);
export const createPoll = (data) => api.post('/polls', data);
export const votePoll = (id, optionIndex) => api.post(`/polls/${id}/vote?option_index=${optionIndex}`);
export const deletePoll = (id) => api.delete(`/polls/${id}`);

// Messages
export const getMessages = (params = {}) => api.get('/messages', { params });

// AI
export const generateAIContent = (data) => api.post('/ai/generate', data);

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);

// Import/Export
export const importContacts = (data) => api.post('/import/contacts', data);
export const exportContacts = () => api.get('/export/contacts');

// A/B Testing
export const getABTests = (params = {}) => api.get('/ab-tests', { params });
export const getABTest = (id) => api.get(`/ab-tests/${id}`);
export const createABTest = (data) => api.post('/ab-tests', data);
export const getABTestResults = (id) => api.get(`/ab-tests/${id}/results`);
export const completeABTest = (id) => api.post(`/ab-tests/${id}/complete`);
export const deleteABTest = (id) => api.delete(`/ab-tests/${id}`);

// Telegram Direct
export const sendTelegramMessage = (data) => api.post('/telegram/send', data);
export const sendBulkTelegram = (data) => api.post('/telegram/send-bulk', data);

// Email Direct
export const sendEmail = (data) => api.post('/email/send', data);
export const sendBulkEmail = (data) => api.post('/email/send-bulk', data);

// Email Tracking
export const getTrackingStats = () => api.get('/tracking/stats');
export const getCampaignTrackingStats = (campaignId) => api.get(`/tracking/campaign/${campaignId}`);
export const getEmailEvents = (params = {}) => api.get('/tracking/events', { params });
export const getRecentEvents = (limit = 10) => api.get('/tracking/events', { params: { limit } });

// Export contacts in different formats
export const exportContactsCSV = () => api.get('/export/contacts?format=csv', { responseType: 'blob' });
export const exportContactsJSON = () => api.get('/export/contacts?format=json');

// Custom Templates
export const getCustomTemplates = () => api.get('/templates');
export const createCustomTemplate = (template) => api.post('/templates', template);
export const getCustomTemplate = (id) => api.get(`/templates/${id}`);
export const updateCustomTemplate = (id, template) => api.put(`/templates/${id}`, template);
export const deleteCustomTemplate = (id) => api.delete(`/templates/${id}`);
export const incrementTemplateUse = (id) => api.post(`/templates/${id}/use`);
export const toggleTemplateFavorite = (id) => api.post(`/templates/${id}/favorite`);

// Subscription API
const mainApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

mainApi.interceptors.request.use((config) => {
  const token = safeLocalStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const activateSubscription = (data) => mainApi.post('/subscription/activate', data);
export const getCurrentSubscription = () => mainApi.get('/subscription/current');
export const getSubscriptionHistory = () => mainApi.get('/subscription/history');
export const toggleAutoRenew = () => mainApi.post('/subscription/toggle-auto-renew');
export const getSubscriptionPlans = () => mainApi.get('/subscription/plans');

export default api;
